(() => {
  const state = {
    entries: [],
    markers: new Map(),
    map: null,
    mapReady: false,
    pendingEntryId: null
  };

  const root = document.querySelector('[data-freezketch-atlas]');
  if (!root) return;

  const dataUrl = root.dataset.dataUrl;
  const mapEl = root.querySelector('#FreezketchAtlasMap');
  const listEl = root.querySelector('[data-atlas-list]');
  const countEl = root.querySelector('[data-atlas-count]');
  const searchEl = root.querySelector('[data-atlas-search]');
  const countryEl = root.querySelector('[data-atlas-country]');
  const architectEl = root.querySelector('[data-atlas-architect]');
  const styleEl = root.querySelector('[data-atlas-style]');
  const collectionEl = root.querySelector('[data-atlas-collection]');
  const worldBounds = [[-85, -180], [85, 180]];

  function validCoord(entry) {
    if (entry.lat === null || entry.lat === undefined || entry.lat === '') return false;
    if (entry.lng === null || entry.lng === undefined || entry.lng === '') return false;
    return Number.isFinite(Number(entry.lat)) && Number.isFinite(Number(entry.lng));
  }

  function uniq(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  function setOptions(select, values, label) {
    if (!select) return;
    select.innerHTML = `<option value="">${escapeHtml(label)}</option>${values
      .map((value) => `<option value="${escapeHtml(value)}">${escapeHtml(value)}</option>`)
      .join('')}`;
  }

  function locationMeta(entry) {
    return [entry.city, entry.country].filter(Boolean).join(', ') || 'Location pending';
  }

  function entryStatus(entry) {
    if (!validCoord(entry)) return 'Location pending';
    if (entry.entry_type === 'personal') return 'Personal entry';
    return 'Mapped';
  }

  function matches(entry) {
    const q = (searchEl?.value || '').toLowerCase().trim();
    const hay = [
      entry.id,
      entry.title,
      entry.architect,
      entry.city,
      entry.country,
      entry.entry_type,
      ...(entry.style || []),
      ...(entry.collections || []),
    ].join(' ').toLowerCase();

    if (q && !hay.includes(q)) return false;
    if (countryEl?.value && entry.country !== countryEl.value) return false;
    if (architectEl?.value && entry.architect !== architectEl.value) return false;
    if (styleEl?.value && !(entry.style || []).includes(styleEl.value)) return false;
    if (collectionEl?.value && !(entry.collections || []).includes(collectionEl.value)) return false;
    return true;
  }

  function pin(entry) {
    return L.divIcon({
      className: '',
      html: `<div class="fk-pin${entry.entry_type === 'personal' ? ' fk-pin--personal' : ''}">${escapeHtml(entry.id.replace('FK-', ''))}</div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -20],
    });
  }

  function popup(entry) {
    const previewUrl = entry.preview_image || entry.hidden_preview_image || '';
    const preview = previewUrl
      ? `<div class="fk-popup-preview"><img src="${escapeHtml(previewUrl)}" alt="${escapeHtml(entry.id)} ${escapeHtml(entry.title)} preview" loading="lazy"></div>`
      : '<div class="fk-popup-preview is-hidden">Preview pending</div>';

    const story = entry.story || {};
    const storyMeta = entry.entry_type === 'personal'
      ? `
        ${story.collected_for ? `<p class="fk-popup-meta">Collected for ${escapeHtml(story.collected_for)}</p>` : ''}
        ${story.quote ? `<p class="fk-popup-meta">“${escapeHtml(story.quote)}”</p>` : ''}
      `
      : '';

    const link = entry.product_url
      ? `<a class="fk-popup-link" href="${escapeHtml(entry.product_url)}">Open Atlas entry</a>`
      : `<span class="fk-popup-link">Collector Entry</span>`;

    return `
      ${preview}
      <span class="fk-badge">${escapeHtml(entry.id)}</span>
      <h2 class="fk-popup-title">${escapeHtml(entry.title)}</h2>
      <p class="fk-popup-meta">${escapeHtml(locationMeta(entry))}<br>${escapeHtml(entry.architect || 'Architect pending')}</p>
      ${storyMeta}
      ${link}
    `;
  }

  function focusEntry(entryId) {
    if (!entryId) return;

    const entry = state.entries.find((candidate) => candidate.id.toLowerCase() === entryId.toLowerCase());
    if (!entry) return;

    const marker = state.markers.get(entry.id);
    if (!state.mapReady || !marker || !validCoord(entry)) {
      state.pendingEntryId = entry.id;
      return;
    }

    marker.addTo(state.map);
    state.map.setView([Number(entry.lat), Number(entry.lng)], Math.max(state.map.getZoom(), 8), {
      animate: true
    });

    setTimeout(() => marker.openPopup(), 350);

    const card = listEl?.querySelector(`[data-id="${CSS.escape(entry.id)}"]`);
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function render() {
    const visible = state.entries.filter(matches);
    const mappedVisible = visible.filter(validCoord);

    if (state.mapReady) {
      state.markers.forEach((marker, id) => {
        const entry = state.entries.find((candidate) => candidate.id === id);
        if (entry && visible.includes(entry)) marker.addTo(state.map);
        else marker.remove();
      });
    }

    if (countEl) countEl.textContent = `${visible.length} entries / ${mappedVisible.length} mapped`;
    if (!listEl) return;

    listEl.innerHTML = visible
      .map((entry) => `
        <button class="fk-entry-card" type="button" data-id="${escapeHtml(entry.id)}">
          <span class="fk-entry-card__top">
            <span class="fk-badge">${escapeHtml(entry.id)}</span>
            <span class="fk-entry-status">${escapeHtml(entryStatus(entry))}</span>
          </span>
          <span>
            <h3 class="fk-entry-title">${escapeHtml(entry.title)}</h3>
            <p class="fk-entry-meta">${escapeHtml(locationMeta(entry))} / ${escapeHtml(entry.architect || 'Architect pending')}</p>
          </span>
        </button>
      `)
      .join('');

    listEl.querySelectorAll('[data-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const entry = state.entries.find((candidate) => candidate.id === button.dataset.id);
        if (!entry) return;
        const marker = state.markers.get(entry.id);

        if (state.mapReady && marker && validCoord(entry)) {
          state.map.setView([Number(entry.lat), Number(entry.lng)], Math.max(state.map.getZoom(), 5));
          marker.openPopup();
        } else if (entry.product_url) {
          window.location.href = entry.product_url;
        }
      });
    });
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if ([...document.scripts].some((script) => script.src === src)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function loadCss(href) {
    if ([...document.styleSheets].some((sheet) => sheet.href === href)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function initFallbackMap() {
    const field = mapEl?.querySelector('[data-static-atlas-map]');
    if (field) field.hidden = false;
  }

  function initLeafletMap() {
    if (!mapEl || typeof L === 'undefined') {
      initFallbackMap();
      return;
    }

    const fallback = mapEl.querySelector('[data-static-atlas-map]');
    if (fallback) fallback.hidden = true;

    state.map = L.map(mapEl, {
      worldCopyJump: false,
      maxBounds: worldBounds,
      maxBoundsViscosity: 1,
      minZoom: 2.2,
    }).setView([25, 0], 2.2);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_3gd9_1_8c8bee56dc7c36846f9c0f18', {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      bounds: worldBounds,
      maxZoom: 20,
      noWrap: true,
      subdomains: 'abcd',
    }).addTo(state.map);

    state.entries.filter(validCoord).forEach((entry) => {
      const marker = L.marker([Number(entry.lat), Number(entry.lng)], {
        icon: pin(entry),
        title: `${entry.id} ${entry.title}`,
      }).bindPopup(popup(entry));

      state.markers.set(entry.id, marker);
    });

    state.mapReady = true;
    const bounds = state.entries.filter(validCoord).map((entry) => [Number(entry.lat), Number(entry.lng)]);

    render();

    if (state.pendingEntryId) {
      focusEntry(state.pendingEntryId);
    } else if (bounds.length) {
      state.map.fitBounds(bounds, { padding: [36, 36] });
    }
  }

  function hydrate(data) {
    state.entries = (data.entries || [])
      .filter((entry) => entry.matched_existing_product || entry.entry_type === 'personal')
      .sort((a, b) => a.id.localeCompare(b.id));

    setOptions(countryEl, uniq(state.entries.map((entry) => entry.country)), 'Country');
    setOptions(architectEl, uniq(state.entries.map((entry) => entry.architect)), 'Architect');
    setOptions(styleEl, uniq(state.entries.flatMap((entry) => entry.style || [])), 'Style');
    setOptions(collectionEl, uniq(state.entries.flatMap((entry) => entry.collections || [])), 'Collection');

    const params = new URLSearchParams(window.location.search);
    const directEntry = params.get('entry');

    if (searchEl && params.get('q')) searchEl.value = params.get('q');
    if (countryEl && params.get('country')) countryEl.value = params.get('country');
    if (architectEl && params.get('architect')) architectEl.value = params.get('architect');
    if (styleEl && params.get('style')) styleEl.value = params.get('style');
    if (collectionEl && params.get('collection')) collectionEl.value = params.get('collection');

    if (directEntry) {
      state.pendingEntryId = directEntry;
      if (searchEl) searchEl.value = directEntry;
    }

    [searchEl, countryEl, architectEl, styleEl, collectionEl].forEach((el) => el && el.addEventListener('input', render));

    render();
    initFallbackMap();

    loadCss('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js')
      .then(initLeafletMap)
      .catch(initFallbackMap);
  }

  fetch(dataUrl)
    .then((response) => {
      if (!response.ok) throw new Error('Atlas data request failed');
      return response.json();
    })
    .then(hydrate)
    .catch(() => {
      if (countEl) countEl.textContent = 'Atlas data unavailable';
      if (listEl) listEl.innerHTML = '<p class="fk-entry-meta">The Atlas database could not be loaded.</p>';
      initFallbackMap();
    });
})();
